// Video utility functions for managing video URLs and load balancing
// Handles subdomain randomization for video servers

// Available video subdomains for load distribution
const VIDEO_SUBDOMAINS = [
  "video", // Original subdomain as fallback
  "clip",
  "zebra",
  "seek",
  "santos",
];

/**
 * Get a random video subdomain from the available list
 * @returns {string} Random subdomain
 */
export const getRandomVideoSubdomain = () => {
  const randomIndex = Math.floor(Math.random() * VIDEO_SUBDOMAINS.length);
  return VIDEO_SUBDOMAINS[randomIndex];
};

/**
 * Replace video URL subdomain if it starts with 'video' or 'clip'
 * @param {string} url - Original video URL
 * @returns {string} URL with replaced subdomain or original URL
 */
export const replaceVideoSubdomain = (url) => {
  if (!url || typeof url !== "string") {
    return url;
  }

  try {
    // Check if URL starts with video or clip subdomain
    const videoPattern =
      /^https:\/\/(video|clip)\.smoker-series\.online\/(.+)$/;
    const match = url.match(videoPattern);

    if (match) {
      const [, currentSubdomain, path] = match;
      const newSubdomain = getRandomVideoSubdomain();
      const newUrl = `https://${newSubdomain}.smoker-series.online/${path}`;

      console.log(`🎬 URL replaced: ${currentSubdomain} → ${newSubdomain}`);
      return newUrl;
    }

    // Return original URL if no match
    return url;
  } catch (error) {
    console.error("❌ Error replacing video subdomain:", error);
    return url;
  }
};

/**
 * Get a deterministic subdomain based on a seed (like episode number or video ID)
 * This ensures consistent subdomain for the same video across sessions
 * @param {string|number} seed - Seed for deterministic selection
 * @returns {string} Subdomain based on seed
 */
export const getDeterministicVideoSubdomain = (seed) => {
  if (!seed) return getRandomVideoSubdomain();

  // Convert seed to string and create simple hash
  const seedStr = seed.toString();
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    const char = seedStr.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  const index = Math.abs(hash) % VIDEO_SUBDOMAINS.length;
  return VIDEO_SUBDOMAINS[index];
};

/**
 * Transform a video URL to use a different subdomain for load balancing
 * @param {string} originalUrl - Original video URL
 * @param {string} method - Method for subdomain selection: 'random', 'deterministic', or 'none'
 * @param {string|number} seed - Seed for deterministic method (optional)
 * @returns {string} Modified URL with new subdomain
 */
export const transformVideoUrl = (
  originalUrl,
  method = "random",
  seed = null
) => {
  if (!originalUrl || typeof originalUrl !== "string") {
    return originalUrl;
  }

  try {
    // Skip transformation for HLS streams
    if (
      originalUrl.toLowerCase().includes("hls") ||
      originalUrl.toLowerCase().includes(".m3u8")
    ) {
      console.log(
        "🎬 Skipping subdomain transformation for HLS stream:",
        originalUrl
      );
      return originalUrl;
    }

    // Check if URL matches the expected pattern
    const urlPattern = /^https:\/\/([^.]+)\.smoker-series\.online\/(.+)$/;
    const match = originalUrl.match(urlPattern);

    if (!match) {
      console.log(
        "🎬 URL does not match expected pattern, returning original:",
        originalUrl
      );
      return originalUrl;
    }

    const [, currentSubdomain, path] = match;
    let newSubdomain;

    switch (method) {
      case "deterministic":
        newSubdomain = getDeterministicVideoSubdomain(seed || path);
        break;
      case "random":
        newSubdomain = getRandomVideoSubdomain();
        break;
      case "none":
      default:
        return originalUrl;
    }

    const newUrl = `https://${newSubdomain}.smoker-series.online/${path}`;

    console.log(
      `🎬 Video URL transformed: ${currentSubdomain} → ${newSubdomain}`
    );
    return newUrl;
  } catch (error) {
    console.error("❌ Error transforming video URL:", error);
    return originalUrl;
  }
};

/**
 * Process all episodes in a movie object to transform video URLs
 * @param {Object} movieData - Movie object with episodes array
 * @param {string} method - Transformation method
 * @returns {Object} Movie object with transformed URLs
 */
export const processMovieVideoUrls = (movieData, method = "deterministic") => {
  console.log("🎬 videoUtils - Processing movie data:", movieData);
  console.log("🎬 videoUtils - Episodes exists:", !!movieData?.episodes);
  console.log(
    "🎬 videoUtils - Episodes is array:",
    Array.isArray(movieData?.episodes)
  );
  console.log("🎬 videoUtils - Episodes length:", movieData?.episodes?.length);

  if (!movieData || !movieData.episodes || !Array.isArray(movieData.episodes)) {
    console.log("🎬 videoUtils - Returning original data (no valid episodes)");
    return movieData;
  }

  const processedMovie = { ...movieData };

  processedMovie.episodes = movieData.episodes.map((episode, index) => {
    console.log(`🎬 videoUtils - Processing episode ${index}:`, episode);
    console.log(`🎬 videoUtils - Episode link:`, episode?.link);

    if (!episode.link) {
      console.log(
        `🎬 videoUtils - Episode ${index} has no link, returning as-is`
      );
      return episode;
    }

    // Use episode number or index as seed for consistent subdomain per episode
    const seed = episode.episode || index + 1;
    const transformedLink = transformVideoUrl(episode.link, method, seed);

    console.log(
      `🎬 videoUtils - Episode ${index} transformed: ${episode.link} → ${transformedLink}`
    );

    return {
      ...episode,
      link: transformedLink,
      originalLink: episode.link, // Keep original for fallback
    };
  });

  console.log(
    `🎬 Processed ${processedMovie.episodes.length} episodes for load balancing`
  );
  return processedMovie;
};

/**
 * Get fallback URL if current URL fails
 * @param {string} currentUrl - Current video URL that failed
 * @param {string} originalUrl - Original URL as fallback
 * @returns {string} Fallback URL
 */
export const getFallbackVideoUrl = (currentUrl, originalUrl) => {
  if (currentUrl === originalUrl) {
    // If original URL failed, try a different subdomain
    return transformVideoUrl(originalUrl, "random");
  }
  // If transformed URL failed, return original
  return originalUrl;
};

/**
 * Check if URL is a video file based on extension
 * @param {string} url - URL to check
 * @returns {boolean} True if URL appears to be a video file
 */
export const isVideoUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  const videoExtensions = [
    ".mp4",
    ".mkv",
    ".avi",
    ".mov",
    ".wmv",
    ".flv",
    ".webm",
    ".m4v",
  ];
  const lowerUrl = url.toLowerCase();

  return (
    videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
    lowerUrl.includes(".m3u8") ||
    lowerUrl.includes("hls")
  );
};

/**
 * Get video server health status (mock function - can be extended with real health checks)
 * @param {string} subdomain - Subdomain to check
 * @returns {Promise<boolean>} Server health status
 */
export const checkServerHealth = async (subdomain) => {
  try {
    // This is a mock implementation
    // In production, you might want to ping the server or check a health endpoint
    // const healthUrl = `https://${subdomain}.smoker-series.online/health`;

    // Mock: randomly return health status for demonstration
    // Replace with actual health check logic
    return Math.random() > 0.1; // 90% uptime simulation
  } catch (error) {
    console.error(`❌ Health check failed for ${subdomain}:`, error);
    return false;
  }
};

/**
 * Get the best available subdomain based on health checks
 * @returns {Promise<string>} Best available subdomain
 */
export const getBestVideoSubdomain = async () => {
  const healthPromises = VIDEO_SUBDOMAINS.map(async (subdomain) => {
    const isHealthy = await checkServerHealth(subdomain);
    return { subdomain, isHealthy };
  });

  try {
    const healthResults = await Promise.all(healthPromises);
    const healthySubdomains = healthResults
      .filter((result) => result.isHealthy)
      .map((result) => result.subdomain);

    if (healthySubdomains.length === 0) {
      console.warn("⚠️ No healthy video servers found, using fallback");
      return "video"; // Fallback to original
    }

    // Return random healthy subdomain
    const randomIndex = Math.floor(Math.random() * healthySubdomains.length);
    return healthySubdomains[randomIndex];
  } catch (error) {
    console.error("❌ Error checking server health:", error);
    return "video"; // Fallback
  }
};
